// ✅ هذا هو ملف "AddFarrierRecordOutside.js"
import React, { useState, useEffect, useCallback } from 'react';

import axios from 'axios';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL;

const AddFarrierRecordOutside = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  //const [chipInput, setChipInput] = useState('');
  const [chipSearchStatus, setChipSearchStatus] = useState('');
  //const chipSearchTimeoutRef = useRef(null);

  const getCurrentDate = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().substring(0, 16);
  };

  const [formData, setFormData] = useState({
    record_date: getCurrentDate(),
    rfid_chip_number: '',
    horse_name: '',
    client_name: '',
    client_phone: '',
    client_id: '',
    procedures: [],
    medications: [],
    payment_type_id: '',
    total_amount: 0,
    notes: '',
  });

  const [procedures, setProcedures] = useState([]);
  const [medications, setMedications] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);
  const calculateTotal = useCallback(() => {
    const proceduresTotal = formData.procedures.reduce((sum, id) => {
      const p = procedures.find((pr) => pr.procedure_id === id);
      return sum + (p?.price || 0);
    }, 0);
    const medsTotal = formData.medications.reduce(
      (sum, m) => sum + (m.price || 0),
      0
    );
    return proceduresTotal + medsTotal;
  }, [formData.procedures, formData.medications, procedures]);
  useEffect(() => {
    const newTotal = calculateTotal();
    setFormData((prev) => ({
      ...prev,
      total_amount: newTotal,
    }));
  }, [calculateTotal]);

  const fetchDropdownData = async () => {
    try {
      const [procRes, medRes, payRes] = await Promise.all([
        axios.get(`${apiUrl}/farrier_procedures`),
        axios.get(`${apiUrl}/farrier_medications`),
        axios.get(`${apiUrl}/payment-types`),
      ]);
      setProcedures(procRes.data);
      setMedications(medRes.data);
      setPaymentTypes(payRes.data);
    } catch (error) {
      console.error('Error loading dropdowns', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProcedureToggle = (procedure_id) => {
    setFormData((prev) => {
      const exists = prev.procedures.includes(procedure_id);
      const updated = exists
        ? prev.procedures.filter((id) => id !== procedure_id)
        : [...prev.procedures, procedure_id];
      return { ...prev, procedures: updated };
    });
  };

  const addMedication = () => {
    setFormData((prev) => ({
      ...prev,
      medications: [
        ...prev.medications,
        { medication_id: '', dosage: '', quantity: '', unit: '', price: 0 },
      ],
    }));
  };

  const updateMedication = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.medications];
      updated[index][field] = value;

      if (field === 'medication_id') {
        const selected = medications.find(
          (m) => m.medication_id === parseInt(value)
        );
        if (selected) {
          updated[index] = {
            ...updated[index],
            dosage: selected.default_dosage || '',
            quantity: selected.default_quantity || 1,
            unit: selected.default_unit || '',
            price: selected.price || 0,
          };
        }
      }
      return { ...prev, medications: updated };
    });
  };

  const removeMedication = (index) => {
    setFormData((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const fetchHorseByChipNumber = async (chipNumber) => {
    const trimmedChip = chipNumber?.trim();
    if (!trimmedChip) {
      setChipSearchStatus('empty');
      return;
    }
    setFormData((prevState) => ({
      ...prevState,
      horse_id: '',
      horse_name: '',
      client_name: '',
      client_phone: '',
      client_id: '',
    }));
    try {
      const response = await axios.get(
        `${apiUrl}/vetclinic-horses/rfid/${trimmedChip}`
      );
      const data = response.data;

      if (data && data.horse_id) {
        setFormData((prevState) => ({
          ...prevState,
          horse_name: data.horse_name || '',
          rfid_chip_number: data.rfid_chip_number || '',
          client_name: data.client_name || '',
          client_phone: data.client_phone || '',
          horse_id: data.horse_id,
          client_id: data.client_id,
        }));
        setChipSearchStatus('found');
      } else {
        setChipSearchStatus('not_found');
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setChipSearchStatus('not_found'); // الخيل غير موجود
      } else {
        console.error('Error fetching horse data:', error);
        setChipSearchStatus('error'); // خطأ حقيقي
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.rfid_chip_number)
      return alert(t('Missing: RFID Chip Number'));
    if (!formData.horse_name) return alert(t('Missing: Horse Name'));
    if (!formData.client_name) return alert(t('Missing: Client Name'));
    if (!formData.client_phone) return alert(t('Missing: Client Phone'));
    if (formData.procedures.length === 0)
      return alert(t('Missing: At least one procedure'));
    if (!formData.payment_type_id) return alert(t('Missing: Payment Type'));

    try {
      if (
        !formData.rfid_chip_number ||
        !formData.horse_name ||
        !formData.client_name ||
        !formData.client_phone ||
        formData.procedures.length === 0 ||
        !formData.payment_type_id
      ) {
        alert(t('Please complete all required fields before submitting.'));
        return;
      }

      const user_id = localStorage.getItem('userId');

      let client_id = formData.client_id;
      if (!client_id && formData.client_name && formData.client_phone) {
        const clientRes = await axios.post(`${apiUrl}/clients`, {
          client_name: formData.client_name,
          mobile: formData.client_phone,
          contact_info: formData.client_phone,
          address: '',
          notes: '',
        });
        client_id = clientRes.data.client_id;
        setFormData((prev) => ({ ...prev, client_id }));
      }

      let horse_id = formData.horse_id;
      if (!horse_id && formData.horse_name && formData.rfid_chip_number) {
        const horseRes = await axios.post(`${apiUrl}/vetclinic-horses`, {
          horse_name: formData.horse_name,
          rfid_chip_number: formData.rfid_chip_number,
          client_id: client_id,
        });
        horse_id = horseRes.data.horse_id;
        setFormData((prev) => ({ ...prev, horse_id }));
      }

      const payload = {
        ...formData,
        work_place: 'Outside',
        user_id,
        procedures: formData.procedures.map((id) => {
          const p = procedures.find((pr) => pr.procedure_id === id);
          return { procedure_id: id, price: p?.price || 0 };
        }),
        medications: formData.medications.map((m) => ({
          ...m,
          price: m.price || 0,
        })),
        total_amount: calculateTotal(),
      };

      console.log('📦 Payload being submitted:', {
        ...payload,
        client_id,
        horse_id,
      });

      await axios.post(`${apiUrl}/farrier-records`, { ...payload, client_id });
      alert(t('Record added successfully!'));
      navigate('/FarrierList');
    } catch (err) {
      console.error('Error saving record:', err);
      alert(
        t(
          'Horse not found or missing data. Please make sure all fields are complete.'
        )
      );
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5">{t('Add Farrier Record (Outside)')}</Typography>
      <Box display="flex" gap={2} alignItems="center" mt={2}>
        <TextField
          label="RFID Chip Number"
          name="rfid_chip_number"
          value={formData.rfid_chip_number}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const chip = formData.rfid_chip_number.trim();
            if (chip) {
              fetchHorseByChipNumber(chip);
            } else {
              alert('يرجى إدخال رقم الشريحة قبل البحث');
            }
          }}
        >
          {t('Search')}
        </Button>
      </Box>
      {chipSearchStatus === 'found' && (
        <Typography color="success.main">✅ تم العثور على الخيل.</Typography>
      )}
      {chipSearchStatus === 'not_found' && (
        <Typography color="warning.main">
          ⚠️ لم يتم العثور على الخيل، سيتم إنشاؤه عند الإرسال.
        </Typography>
      )}
      {chipSearchStatus === 'error' && (
        <Typography color="error">❌ خطأ في الاتصال بالسيرفر.</Typography>
      )}
      {chipSearchStatus === 'empty' && (
        <Typography color="warning.main">⚠️ رقم الشريحة فارغ.</Typography>
      )}
      <TextField
        label={t('Horse Name')}
        name="horse_name"
        value={formData.horse_name}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />
      <TextField
        label={t('Client Name')}
        name="client_name"
        value={formData.client_name}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />
      <TextField
        label={t('Client Phone')}
        name="client_phone"
        value={formData.client_phone}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <Typography variant="subtitle1" mt={2}>
        {t('Procedures')}
      </Typography>
      <Grid container spacing={2}>
        {procedures.map((proc) => (
          <Grid item xs={6} sm={4} key={proc.procedure_id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.procedures.includes(proc.procedure_id)}
                  onChange={() => handleProcedureToggle(proc.procedure_id)}
                />
              }
              label={proc.procedure_name}
            />
          </Grid>
        ))}
      </Grid>
      {false && (
        <>
          <Typography variant="subtitle1" mt={2}>
            {t('Medications')}
          </Typography>
          {formData.medications.map((med, idx) => (
            <Box key={idx} mb={2}>
              <Select
                fullWidth
                value={med.medication_id}
                onChange={(e) =>
                  updateMedication(idx, 'medication_id', e.target.value)
                }
              >
                {medications.map((m) => (
                  <MenuItem key={m.medication_id} value={m.medication_id}>
                    {m.medication_name}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label={t('Dosage')}
                value={med.dosage}
                onChange={(e) =>
                  updateMedication(idx, 'dosage', e.target.value)
                }
                fullWidth
                margin="dense"
              />
              <TextField
                label={t('Quantity')}
                value={med.quantity}
                onChange={(e) =>
                  updateMedication(idx, 'quantity', e.target.value)
                }
                fullWidth
                margin="dense"
              />
              <TextField
                label={t('Unit')}
                value={med.unit}
                onChange={(e) => updateMedication(idx, 'unit', e.target.value)}
                fullWidth
                margin="dense"
              />
              <Button color="error" onClick={() => removeMedication(idx)}>
                {t('Remove Medication')}
              </Button>
            </Box>
          ))}
          <Button variant="outlined" onClick={addMedication}>
            {t('Add Medication')}
          </Button>

          <TextField
            label="Total Amount"
            name="total_amount"
            type="number"
            value={formData.total_amount}
            fullWidth
            margin="normal"
            disabled
          />
        </>
      )}
      <FormControl fullWidth margin="dense">
        <InputLabel>{t('Payment Type')}</InputLabel>
        <Select
          name="payment_type_id"
          value={formData.payment_type_id}
          onChange={handleChange}
        >
          {paymentTypes.map((pt) => (
            <MenuItem key={pt.payment_type_id} value={pt.payment_type_id}>
              {pt.payment_type_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label={t('Notes')}
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        fullWidth
        multiline
        rows={3}
        margin="dense"
      />

      <Box mt={2} display="flex" justifyContent="center">
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('Submit')}
        </Button>
      </Box>
    </Box>
  );
};

export default AddFarrierRecordOutside;
